import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back.");
      if (u?.role === "therapist") navigate("/therapist");
      else if (u?.role === "admin") navigate("/admin");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="page-login">
      <div className="hidden lg:flex items-center justify-center bg-mindora-panel border-r border-mindora-line p-16">
        <div className="max-w-md">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-6">
            <span className="editorial-rule" />welcome back
          </div>
          <h1 className="font-serif text-5xl text-mindora-ink leading-tight">
            The page is still here.<br /><em className="italic text-mindora-red">So is the feeling.</em>
          </h1>
          <p className="mt-6 text-mindora-muted leading-relaxed">
            Pick up where you left off — your stories, your gratitudes, your letters waiting to be sent.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={handle} className="w-full max-w-md" data-testid="login-form">
          <h2 className="font-serif text-3xl text-mindora-ink">Sign in</h2>
          <p className="text-sm text-mindora-muted mt-2">Welcome back to your quiet corner.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-mindora-ink/80 text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
                placeholder="you@somewhere.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-mindora-ink/80 text-xs uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="mt-7 w-full btn-primary rounded-full h-11"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <p className="mt-6 text-sm text-mindora-muted text-center">
            New here?{" "}
            <Link to="/register" className="text-mindora-red hover:underline" data-testid="login-to-register-link">
              Create your space
            </Link>
          </p>

          <div className="mt-10 pt-6 border-t border-mindora-line text-center text-xs text-mindora-muted">
            Are you a counselor?{" "}
            <Link to="/counselor/login" className="text-mindora-red hover:underline" data-testid="login-to-counselor-link">
              Sign in to your practice portal
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
